import { useLayoutEffect, useEffect, useState, useContext } from 'react';

import { HeadContext } from '~/state/head';

import CharacterServiceApi from '../api/CharacterServiceApi';
import useLocalStorage from '../hooks/useLocalStorage';
import useUserId from '../hooks/useUserId';
import useUserName from '../hooks/useUserName';
import './MyCharacters.scss';
import { SessionNames } from './analytics/constants';
import { initAnalytics } from './analytics/utils';
import { Header } from './components/Header';
import MyCharactersListing from './components/MyCharactersListing';
import PlayerAppBanner from './components/PlayerAppBanner';
import { CharacterStatusEnum, QueryResponseData } from './typings';
import CharacterUtils from './utils/CharacterUtils';

interface Props {
    characterQuery: {
        isLoading: boolean;
        data: QueryResponseData | undefined;
        refetch: () => void;
        error: Error | null;
    };
}

const MyCharacters = ({ characterQuery }: Props) => {
    useEffect(() => {
        initAnalytics(SessionNames.DDB);
    }, []);
    const userId = useUserId();
    const userName = useUserName();
    const { setTitle } = useContext(HeadContext);
    const [storeUrls, setStoreUrls] = useState({ appleStoreLink: '', googlePlayStoreLink: '' });
    useLayoutEffect(() => {
        document
            .getElementsByTagName('body')[0]
            .classList.add('site', 'site-dndbeyond', 'site-www', 'body-mycontent', 'body-mycontent-characterindex');
        return () => {
            document
                .getElementsByTagName('body')[0]
                .classList.remove(
                    'site',
                    'site-dndbeyond',
                    'site-www',
                    'body-mycontent',
                    'body-mycontent-characterindex',
                );
        };
    }, []);

    useEffect(() => {
        async function getFlags() {
            try {
                const flagResponse = await CharacterServiceApi.getFeatureFlagsFromCharacterService([
                    'players-app-apple-store-link',
                    'players-app-google-play-store-link',
                ]);
                const { data } = await flagResponse.json();
                setStoreUrls({
                    appleStoreLink: data['players-app-apple-store-link'],
                    googlePlayStoreLink: data['players-app-google-play-store-link'],
                });
            } catch (e) {
                /* no-op */
            }
        }
        getFlags();
    }, []);

    useEffect(() => {
        setTitle('My Characters');
    }, [setTitle]);

    const [playerAppBannerDismissed, setPlayerAppBannerDismissed] = useLocalStorage(
        `MY_CHARACTERS_PLAYER_APP_BANNER_DISMISSED:${userId}`,
        false,
    );

    const showPlayerAppBanner: boolean = !playerAppBannerDismissed;

    const { isLoading, data, refetch, error } = characterQuery;
    const { characters, hasLockedCharacters, maxCharacterSlotsAllowed } = CharacterUtils.generateAppData(
        userName,
        data,
    );
    // Check for max active characters
    const hasMaxCharacters =
        characters?.filter((character) => CharacterUtils.getStatus(character) === CharacterStatusEnum.Active).length >=
        (maxCharacterSlotsAllowed ?? Infinity);

    return (
        <div className="my-characters-wrapper">
            <Header hasMaxCharacters={hasMaxCharacters} />
            <div className="content">
                {showPlayerAppBanner && (
                    <PlayerAppBanner
                        appleStoreLink={storeUrls.appleStoreLink}
                        googlePlayStoreLink={storeUrls.googlePlayStoreLink}
                        onDismiss={() => setPlayerAppBannerDismissed(true)}
                    />
                )}
                <MyCharactersListing
                    className="ddbcl-my-characters-listing"
                    hasLockedCharacters={hasLockedCharacters}
                    maxCharacterSlotsAllowed={maxCharacterSlotsAllowed}
                    characters={characters}
                    isLoading={isLoading}
                    hasMaxCharacters={hasMaxCharacters}
                    reload={refetch}
                    loadingError={error}
                />
                {showPlayerAppBanner && (
                    <PlayerAppBanner
                        appleStoreLink={storeUrls.appleStoreLink}
                        googlePlayStoreLink={storeUrls.googlePlayStoreLink}
                        onDismiss={() => setPlayerAppBannerDismissed(true)}
                    />
                )}
            </div>
        </div>
    );
};

export default MyCharacters;
